import React, { useState } from 'react';
import {
  BellRing,
  Clock,
  Fingerprint,
  KeyRound,
  Pencil,
  Plus,
  RefreshCw,
  ScanFace,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/loading-states';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  useBioAgents,
  useBioTerminals,
  useCreateBioCommand,
  useRotateBioAgentToken,
} from '../../hooks/useBio';
import {
  useTenantSettings,
  useUpdateTenantSettings,
} from '../../hooks/settings/useSettings';
import { BioAgentDialog, BioAgentTokenDialog } from './BioAgentDialog';
import { BioTerminalDialog } from './BioTerminalDialog';
import type { BioAgent, BioTerminal } from '../../types/bio.types';

const formatDateTime = (value: string | null): string =>
  value ? new Date(value).toLocaleString('ru-RU') : '—';

/** Дрейф часов: до минуты — норма, дальше стоит показать величину. */
const formatDrift = (seconds: number | null): string => {
  if (seconds === null) return 'не измерялся';
  if (Math.abs(seconds) < 60) return `${seconds} с`;
  return `${Math.round(seconds / 60)} мин`;
};

export const BioTerminalsCard: React.FC = () => {
  // На экране настроек статус связи должен быть живым.
  const { data: agents, isLoading: isLoadingAgents } = useBioAgents({
    refetchInterval: 60 * 1000,
  });
  const { data: terminals, isLoading: isLoadingTerminals } = useBioTerminals({
    refetchInterval: 60 * 1000,
  });
  const rotateToken = useRotateBioAgentToken();
  const createCommand = useCreateBioCommand();
  const { data: settings, isLoading: isLoadingSettings } = useTenantSettings();
  const updateSettings = useUpdateTenantSettings();
  // Нет значения в ответе (старый API) — на сервере действует умолчание «вкл».
  const arrivalPushEnabled = settings?.bio_arrival_push_enabled ?? true;

  const handleArrivalPushToggle = async (checked: boolean) => {
    try {
      await updateSettings.mutateAsync({ bio_arrival_push_enabled: checked });
    } catch (error) {
      const message = (error as { message?: string })?.message;
      window.alert(message || 'Не удалось сохранить настройку');
    }
  };

  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<BioAgent | null>(null);
  const [terminalDialogOpen, setTerminalDialogOpen] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState<BioTerminal | null>(
    null,
  );
  const [issuedToken, setIssuedToken] = useState<{
    token: string;
    agentName: string;
  } | null>(null);
  const [syncedTerminalId, setSyncedTerminalId] = useState<string | null>(null);

  const openNewAgent = () => {
    setEditingAgent(null);
    setAgentDialogOpen(true);
  };

  const openEditAgent = (agent: BioAgent) => {
    setEditingAgent(agent);
    setAgentDialogOpen(true);
  };

  const openNewTerminal = () => {
    setEditingTerminal(null);
    setTerminalDialogOpen(true);
  };

  const openEditTerminal = (terminal: BioTerminal) => {
    setEditingTerminal(terminal);
    setTerminalDialogOpen(true);
  };

  const handleRotateToken = async (agent: BioAgent) => {
    const confirmed = window.confirm(
      `Перевыпустить токен агента «${agent.name}»?\n\n` +
        'Старый токен сразу перестанет работать — агент на объекте не сможет ' +
        'работать, пока ему не пропишут новый.',
    );
    if (!confirmed) return;
    try {
      const result = await rotateToken.mutateAsync(agent.id);
      setIssuedToken({ token: result.token, agentName: agent.name });
    } catch (error) {
      const message = (error as { message?: string })?.message;
      window.alert(message || 'Не удалось перевыпустить токен');
    }
  };

  const handleSyncTime = async (terminal: BioTerminal) => {
    try {
      await createCommand.mutateAsync({
        terminal_id: terminal.id,
        command_type: 'sync_time',
      });
      setSyncedTerminalId(terminal.id);
      setTimeout(() => setSyncedTerminalId(null), 4000);
    } catch (error) {
      const message = (error as { message?: string })?.message;
      window.alert(message || 'Не удалось поставить команду');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Fingerprint className="h-5 w-5" />
                Биометрические терминалы
              </CardTitle>
              <CardDescription>
                Агенты объектов и устройства распознавания лиц. Проходы
                попадают в табель наравне с отметками из мобильного приложения.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={openNewAgent}>
                <Plus className="mr-2 h-4 w-4" />
                Агент
              </Button>
              <Button
                size="sm"
                onClick={openNewTerminal}
                disabled={!agents || agents.length === 0}
                title={
                  !agents || agents.length === 0
                    ? 'Сначала создайте агента объекта'
                    : undefined
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Терминал
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Пуш сотруднику о первом проходе за день */}
          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-0.5">
              <Label
                htmlFor="bio-arrival-push"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <BellRing className="h-4 w-4" />
                Пуш сотруднику при первом проходе за день
              </Label>
              <p className="text-xs text-muted-foreground">
                Сотрудник с установленным приложением получит уведомление
                «Приход отмечен», если проход через терминал — его первая
                отметка прихода за день. Проходы, доставленные агентом позже
                30 минут, уведомлений не вызывают.
              </p>
            </div>
            <Switch
              id="bio-arrival-push"
              checked={arrivalPushEnabled}
              onCheckedChange={handleArrivalPushToggle}
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* Агенты */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              Агенты объектов
            </h3>

            {isLoadingAgents ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner />
              </div>
            ) : !agents || agents.length === 0 ? (
              <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Агентов пока нет. Агент — программа на компьютере объекта; после
                создания вы получите токен для её настройки.
              </p>
            ) : (
              <div className="space-y-2">
                {agents.map((agent) => (
                  <div
                    key={agent.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{agent.name}</span>
                        {agent.online ? (
                          <Badge variant="success">На связи</Badge>
                        ) : (
                          <Badge variant="secondary">Нет связи</Badge>
                        )}
                        {!agent.is_active && (
                          <Badge variant="outline">Выключен</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {agent.location?.name || '—'}
                        {agent.version ? ` · версия ${agent.version}` : ''} ·
                        heartbeat {formatDateTime(agent.last_heartbeat_at)}
                        {agent.pending_events_count
                          ? ` · очередь ${agent.pending_events_count}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRotateToken(agent)}
                        disabled={rotateToken.isPending}
                        title="Перевыпустить токен"
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditAgent(agent)}
                        title="Изменить"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Терминалы */}
          <div className="space-y-2 border-t pt-4">
            <h3 className="text-sm font-medium text-muted-foreground">
              Устройства
            </h3>

            {isLoadingTerminals ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner />
              </div>
            ) : !terminals || terminals.length === 0 ? (
              <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Терминалов пока нет. Понадобятся серийный номер, адрес в
                локальной сети объекта и учётные данные ISAPI.
              </p>
            ) : (
              <div className="space-y-2">
                {terminals.map((terminal) => (
                  <div
                    key={terminal.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <ScanFace className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium break-all">
                          {terminal.model || terminal.serial_number}
                        </span>
                        <Badge variant="outline">
                          {terminal.direction === 'in'
                            ? 'Вход'
                            : terminal.direction === 'out'
                              ? 'Выход'
                              : 'Универсальный'}
                        </Badge>
                        {!terminal.is_active && (
                          <Badge variant="secondary">Выключен</Badge>
                        )}
                        {!terminal.agent_id && (
                          <Badge variant="warning">Без агента</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground break-all">
                        {terminal.host}:{terminal.port} ·{' '}
                        {terminal.agent?.name || 'агент не назначен'} ·{' '}
                        {terminal.location?.name || '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Зарегистрировано лиц: {terminal.enrolled_count ?? 0}
                        {terminal.capacity_faces
                          ? ` из ${terminal.capacity_faces}`
                          : ''}{' '}
                        · часы: {formatDrift(terminal.clock_drift_seconds)}
                        {terminal.device_timezone
                          ? `, пояс ${terminal.device_timezone}`
                          : ''}{' '}
                        · связь: {formatDateTime(terminal.last_seen_at)}
                      </p>
                      {syncedTerminalId === terminal.id && (
                        <p className="text-xs text-green-600">
                          Команда синхронизации времени поставлена в очередь
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSyncTime(terminal)}
                        disabled={!terminal.agent_id || createCommand.isPending}
                        title="Синхронизировать время"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditTerminal(terminal)}
                        title="Изменить"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100">
            <RefreshCw className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Порядок запуска: создать агента → передать его токен тому, кто
              настраивает компьютер на объекте → зарегистрировать терминал и
              привязать к агенту. Синхронизацию времени сервер поставит сам.
            </span>
          </div>
        </CardContent>
      </Card>

      <BioAgentDialog
        open={agentDialogOpen}
        onOpenChange={setAgentDialogOpen}
        agent={editingAgent}
        onTokenIssued={(token, agentName) =>
          setIssuedToken({ token, agentName })
        }
      />

      <BioTerminalDialog
        open={terminalDialogOpen}
        onOpenChange={setTerminalDialogOpen}
        terminal={editingTerminal}
      />

      <BioAgentTokenDialog
        open={issuedToken !== null}
        onOpenChange={(open) => {
          if (!open) setIssuedToken(null);
        }}
        token={issuedToken?.token ?? ''}
        agentName={issuedToken?.agentName ?? ''}
      />
    </>
  );
};
